import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Icons from '@mui/icons-material';
import { sendRequest } from '../utils/dateHelpers';
import AssistantArtifactRenderer from './Assistant/AssistantArtifactRenderer';
import { Skeleton } from './ui';
import { getChatbotContext } from '../constants/chatbotContext';
import {
  DEFAULT_ASSISTANT_STARTERS,
  extractAssistantData,
  makeAssistantMessage,
  makeAssistantUserMessage,
  mergeAssistantStarters,
  getAssistantMessageEndpoint,
  MINI_ASSISTANT_SESSION_STORAGE_KEY
} from '../utils/aiAssistantClient';

const HALO_ANIMATION = 'gitme-copilot-halo 3.8s ease-in-out infinite';
const HALO_DELAYED_ANIMATION =
  'gitme-copilot-halo 3.8s ease-in-out 1.9s infinite';

const ChatbotAgent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState([]);
  const [starters, setStarters] = useState(DEFAULT_ASSISTANT_STARTERS);
  const [question, setQuestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [approvalBusy, setApprovalBusy] = useState(false);
  const [error, setError] = useState('');
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const pageContext = useMemo(
    () => getChatbotContext(location.pathname),
    [location.pathname]
  );
  const visibleStarters = useMemo(
    () => mergeAssistantStarters(pageContext.starters, starters).slice(0, 5),
    [pageContext.starters, starters]
  );

  const rememberSession = nextSessionId => {
    if (!nextSessionId) return;
    window.sessionStorage.setItem(
      MINI_ASSISTANT_SESSION_STORAGE_KEY,
      nextSessionId
    );
    setSessionId(nextSessionId);
  };

  const loadSession = async nextSessionId => {
    if (!nextSessionId) return '';
    const response = await sendRequest(
      `/ai-assistant/sessions/${encodeURIComponent(nextSessionId)}/messages`,
      'GET'
    );
    const data = extractAssistantData(response);
    rememberSession(nextSessionId);
    setMessages(data.messages || []);
    setStarters(data.starters || DEFAULT_ASSISTANT_STARTERS);
    return nextSessionId;
  };

  const ensureSession = async () => {
    const rememberedSessionId =
      sessionId ||
      window.sessionStorage.getItem(MINI_ASSISTANT_SESSION_STORAGE_KEY);
    try {
      return await loadSession(rememberedSessionId);
    } catch {
      window.sessionStorage.removeItem(MINI_ASSISTANT_SESSION_STORAGE_KEY);
      setSessionId('');
      return '';
    }
  };

  useEffect(() => {
    if (!isOpen || sessionId || isLoading) return;
    const rememberedSessionId = window.sessionStorage.getItem(
      MINI_ASSISTANT_SESSION_STORAGE_KEY
    );
    if (!rememberedSessionId) return;
    setIsLoading(true);
    loadSession(rememberedSessionId)
      .catch(err => {
        window.sessionStorage.removeItem(MINI_ASSISTANT_SESSION_STORAGE_KEY);
        setSessionId('');
        setError(err?.message || 'Ask AI could not load.');
      })
      .finally(() => setIsLoading(false));
  }, [isOpen, sessionId, isLoading]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const timeoutId = window.setTimeout(() => inputRef.current?.focus(), 100);
    return () => window.clearTimeout(timeoutId);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [isOpen, messages, isSending]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);
    updatePreference();
    mediaQuery.addEventListener?.('change', updatePreference);
    return () => mediaQuery.removeEventListener?.('change', updatePreference);
  }, []);

  const sendMessage = async text => {
    const trimmed = text.trim();
    if (!trimmed || isSending) return;
    setQuestion('');
    setIsSending(true);
    setError('');
    setMessages(current => [...current, makeAssistantUserMessage(trimmed)]);

    try {
      const activeSessionId = sessionId || (await ensureSession());
      const response = await sendRequest(
        getAssistantMessageEndpoint(activeSessionId),
        'POST',
        { message: trimmed }
      );
      const data = extractAssistantData(response);
      if (!activeSessionId && data.sessionId) {
        rememberSession(data.sessionId);
      }
      setMessages(current => [...current, makeAssistantMessage(data)]);
      setStarters(data.starters || DEFAULT_ASSISTANT_STARTERS);
    } catch (err) {
      setMessages(current => current.slice(0, -1));
      setError(err?.message || 'Ask AI could not generate a response.');
    } finally {
      setIsSending(false);
    }
  };

  const resolveApproval = async (message, approval, approved) => {
    if (!sessionId || approvalBusy) return;
    setApprovalBusy(true);
    try {
      const response = await sendRequest(
        `/ai-assistant/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(message.id)}/approval`,
        'POST',
        { approvalId: approval.id, approved }
      );
      const data = extractAssistantData(response);
      setMessages(current =>
        current.map(item =>
          item.id === message.id
            ? makeAssistantMessage({ ...item, ...data })
            : item
        )
      );
    } catch (err) {
      setError(err?.message || 'Ask AI could not process this approval.');
    } finally {
      setApprovalBusy(false);
    }
  };

  const resolvePlan = async (message, plan, action) => {
    if (!sessionId || !plan?.id || approvalBusy) return;
    setApprovalBusy(true);
    try {
      const actionPath = action === 'continue' ? 'continuation' : 'approve';
      const response = await sendRequest(
        `/ai-assistant/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(message.id)}/plans/${encodeURIComponent(plan.id)}/${actionPath}`,
        'POST',
        {}
      );
      const data = extractAssistantData(response);
      setMessages(current =>
        current.map(item =>
          item.id === message.id
            ? makeAssistantMessage({ ...item, ...data })
            : item
        )
      );
      setStarters(data.starters || DEFAULT_ASSISTANT_STARTERS);
    } catch (err) {
      setError(err?.message || 'Ask AI could not process this plan.');
    } finally {
      setApprovalBusy(false);
    }
  };

  const openFullAssistant = () => {
    navigate(
      sessionId
        ? `/ai-assistant?sessionId=${encodeURIComponent(sessionId)}`
        : '/ai-assistant'
    );
  };

  const handleNewChat = () => {
    if (isSending || isLoading) return;
    setError('');
    setSessionId('');
    setMessages([]);
    window.sessionStorage.removeItem(MINI_ASSISTANT_SESSION_STORAGE_KEY);
  };

  return (
    <div className="fixed bottom-4 right-4 z-9999 sm:bottom-6 sm:right-6">
      <style>{`@keyframes gitme-copilot-halo { 0%, 100% { opacity: .3; transform: scale(.96); } 50% { opacity: .62; transform: scale(1.22); } }`}</style>
      {isOpen && (
        <section
          id="gitme-copilot-panel"
          className={`mb-4 flex w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-stroke bg-white shadow-6 transition-[max-width,height] duration-300 dark:border-strokedark dark:bg-boxdark ${
            isExpanded
              ? 'h-[min(760px,calc(100vh-7.5rem))] max-w-[680px]'
              : 'h-[min(640px,calc(100vh-7.5rem))] max-w-[420px]'
          }`}
          aria-label="Gitzy Ask AI"
        >
          <header className="flex items-start justify-between gap-3 border-b border-stroke px-4 py-3 dark:border-strokedark">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-black dark:text-white">
                Gitzy
              </p>
              <p className="mt-1 truncate text-xs text-body dark:text-bodydark">
                {pageContext.description}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={openFullAssistant}
                className="rounded border border-primary/30 px-2 py-1 text-xs font-medium text-primary hover:border-primary"
                title="Open Ask AI"
              >
                Open
              </button>
              <button
                type="button"
                onClick={() => setIsExpanded(value => !value)}
                className="flex h-7 w-7 items-center justify-center rounded border border-stroke text-body hover:border-primary hover:text-primary dark:border-strokedark dark:text-bodydark"
                title={isExpanded ? 'Use compact chat' : 'Expand chat'}
                aria-label={isExpanded ? 'Use compact chat' : 'Expand chat'}
              >
                {isExpanded ? (
                  <Icons.CloseFullscreen fontSize="small" />
                ) : (
                  <Icons.OpenInFull fontSize="small" />
                )}
              </button>
              <button
                type="button"
                onClick={handleNewChat}
                disabled={isSending || isLoading}
                className="flex h-7 w-7 items-center justify-center rounded border border-stroke text-body hover:border-primary hover:text-primary disabled:opacity-50 dark:border-strokedark dark:text-bodydark"
                title="New chat"
                aria-label="New chat"
              >
                <Icons.Add fontSize="small" />
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="flex h-7 w-7 items-center justify-center rounded border border-stroke text-body hover:border-primary hover:text-primary dark:border-strokedark dark:text-bodydark"
                title="Close chat"
                aria-label="Close chat"
              >
                <Icons.Close fontSize="small" />
              </button>
            </div>
          </header>

          {error && (
            <div className="border-b border-danger/20 bg-danger/5 px-4 py-2 text-xs text-danger">
              {error}
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto bg-gray-2 px-4 py-4 dark:bg-boxdark-2">
            {isLoading ? (
              <div className="space-y-2 rounded-lg bg-white p-3 dark:bg-boxdark">
                <Skeleton className="h-3 w-2/3" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ) : (
              <div className="space-y-3">
                {messages.length === 0 && (
                  <p className="rounded-lg border border-dashed border-stroke bg-white px-3 py-4 text-sm text-body dark:border-strokedark dark:bg-boxdark dark:text-bodydark">
                    Ask Gitzy about this page or your engineering data.
                  </p>
                )}
                {messages.map(message => {
                  const isUser = message.role === 'user';
                  const approval =
                    message.pendingAction || message.pendingApprovals?.[0];
                  const plan = message.pendingPlan;
                  return (
                    <div key={message.id} className="space-y-2">
                      {message.artifacts?.map(artifact => (
                        <div key={artifact.id} className="overflow-x-auto">
                          <AssistantArtifactRenderer artifact={artifact} />
                        </div>
                      ))}
                      <div
                        className={`max-w-[92%] rounded-lg px-3 py-2 text-sm leading-6 ${isUser ? 'ml-auto bg-primary text-white' : 'border border-stroke bg-white text-black dark:border-strokedark dark:bg-boxdark dark:text-white'}`}
                      >
                        <p className="whitespace-pre-wrap break-words">
                          {message.content || message.answer}
                        </p>
                        {message.screens?.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {message.screens.map(screen => (
                              <a
                                key={screen.url}
                                href={screen.url}
                                className="rounded border border-primary/20 bg-primary/5 px-2 py-1 text-xs text-primary"
                                target="_blank"
                                rel="noreferrer"
                              >
                                {screen.label || screen.title || 'Open screen'}
                              </a>
                            ))}
                          </div>
                        )}
                        {approval && (
                          <div className="mt-3 flex flex-wrap items-center gap-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-950">
                            <span className="flex-1">
                              {approval.summary || 'Apply this change?'}
                            </span>
                            <button
                              type="button"
                              disabled={approvalBusy}
                              onClick={() =>
                                resolveApproval(message, approval, true)
                              }
                              className="rounded bg-primary px-2 py-1 text-white disabled:opacity-50"
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              disabled={approvalBusy}
                              onClick={() =>
                                resolveApproval(message, approval, false)
                              }
                              className="rounded border border-amber-400 px-2 py-1 disabled:opacity-50"
                            >
                              Decline
                            </button>
                          </div>
                        )}
                        {plan && (
                          <div className="mt-3 rounded border border-primary/25 bg-primary/5 p-2 text-xs text-black dark:text-white">
                            <p className="font-semibold">Workspace plan</p>
                            {plan.summary && (
                              <p className="mt-1 text-body dark:text-bodydark">
                                {plan.summary}
                              </p>
                            )}
                            <div className="mt-2 flex flex-wrap gap-2">
                              {plan.status === 'pending_approval' && (
                                <button
                                  type="button"
                                  disabled={approvalBusy}
                                  onClick={() =>
                                    resolvePlan(message, plan, 'approve')
                                  }
                                  className="rounded bg-primary px-2 py-1 text-white disabled:opacity-50"
                                >
                                  Approve plan
                                </button>
                              )}
                              {plan.continuationAvailable && (
                                <button
                                  type="button"
                                  disabled={approvalBusy}
                                  onClick={() =>
                                    resolvePlan(message, plan, 'continue')
                                  }
                                  className="rounded border border-primary px-2 py-1 text-primary disabled:opacity-50"
                                >
                                  Continue plan
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {isSending && (
                  <div className="mr-auto rounded-lg border border-stroke bg-white px-3 py-2 text-sm text-body dark:border-strokedark dark:bg-boxdark dark:text-bodydark">
                    Gitzy is thinking...
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          <footer className="border-t border-stroke bg-white p-4 dark:border-strokedark dark:bg-boxdark">
            <div className="mb-3 flex max-h-20 flex-wrap gap-2 overflow-y-auto pr-1">
              {visibleStarters.map(starter => (
                <button
                  key={starter}
                  type="button"
                  disabled={isSending || isLoading}
                  onClick={() => sendMessage(starter)}
                  className="rounded border border-stroke px-2 py-1 text-left text-xs text-body hover:border-primary hover:text-primary disabled:opacity-50 dark:border-strokedark dark:text-bodydark"
                >
                  {starter}
                </button>
              ))}
            </div>
            <form
              onSubmit={event => {
                event.preventDefault();
                sendMessage(question);
              }}
            >
              <label className="sr-only" htmlFor="chatbot-question">
                Message Ask AI
              </label>
              <div className="flex items-end gap-2 rounded-lg border border-stroke p-2 focus-within:border-primary dark:border-strokedark">
                <textarea
                  id="chatbot-question"
                  ref={inputRef}
                  value={question}
                  onChange={event => setQuestion(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      sendMessage(question);
                    }
                  }}
                  rows={2}
                  placeholder="Ask Gitzy..."
                  className="min-h-[48px] flex-1 resize-none bg-transparent px-2 py-1 text-sm text-black outline-none dark:text-white"
                />
                <button
                  type="submit"
                  disabled={!question.trim() || isSending || isLoading}
                  className="flex h-9 w-9 items-center justify-center rounded bg-primary text-white disabled:opacity-50"
                  aria-label="Send message"
                >
                  <Icons.Send fontSize="small" />
                </button>
              </div>
            </form>
          </footer>
        </section>
      )}
      <div className="relative ml-auto h-11 w-11">
        {!isOpen && (
          <>
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -inset-2 rounded-2xl bg-primary/30 blur-md"
              style={{
                animation: prefersReducedMotion ? 'none' : HALO_ANIMATION
              }}
            />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -inset-3 rounded-[20px] border border-primary/50"
              style={{
                animation: prefersReducedMotion
                  ? 'none'
                  : HALO_DELAYED_ANIMATION
              }}
            />
          </>
        )}
        <button
          type="button"
          className="relative z-10 ml-auto flex h-11 w-11 items-center justify-center rounded-lg bg-primary text-white shadow-6 transition hover:bg-opacity-90"
          aria-label="Open Gitzy Ask AI"
          aria-expanded={isOpen}
          aria-controls="gitme-copilot-panel"
          onClick={() => setIsOpen(value => !value)}
        >
          <Icons.AutoAwesome fontSize="small" />
        </button>
      </div>
    </div>
  );
};

export default ChatbotAgent;
